import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Share2,
  Check,
  Sparkles,
  ExternalLink,
  ChevronRight,
  CheckSquare,
  Square,
  Zap,
  Bot,
  Search,
  Layers,
  ArrowUpRight,
  ShieldCheck,
  AlertCircle
} from "lucide-react";

export default function WhyRankOnGoogleNotChatGPTPostPage() {
  const [copied, setCopied] = useState(false);
  const [checkedAuditSteps, setCheckedAuditSteps] = useState({});

  const toggleAuditStep = (id) => {
    setCheckedAuditSteps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const originalTitle = document.title;
    const pageTitle = "Why Do I Rank on Google but Don't Appear in ChatGPT? | Kanna Narendra Kumar";
    const pageDesc = "Ranking on Google and being visible in AI search are not the same thing. Discover the 3 core reasons your competitors appear in ChatGPT while you don't, how to solve fan-out queries, and the 6-step AI visibility audit protocol.";
    const pageUrl = "https://narendradigital.in/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/";

    document.title = pageTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    setMeta('meta[name="description"]', "content", pageDesc);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDesc);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDesc);
    setMeta('meta[name="twitter:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    canonical.setAttribute("href", pageUrl);

    // Inject JSON-LD Schema: TechArticle + BreadcrumbList + FAQPage
    const scriptId = "why-rank-google-not-chatgpt-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            "@id": `${pageUrl}#article`,
            "isPartOf": {
              "@type": "WebSite",
              "@id": "https://narendradigital.in/#website",
              "name": "Kanna Narendra Kumar — Senior SEO & AI Search Strategist",
              "url": "https://narendradigital.in"
            },
            "headline": "Why Do I Rank on Google but Don't Appear in ChatGPT?",
            "description": pageDesc,
            "image": "https://narendradigital.in/og-image-v3.jpg",
            "datePublished": "2026-10-07T10:00:00+05:30",
            "dateModified": "2026-10-07T15:30:00+05:30",
            "mainEntityOfPage": pageUrl,
            "author": {
              "@type": "Person",
              "@id": "https://narendradigital.in/#author",
              "name": "Kanna Narendra Kumar",
              "jobTitle": "Senior SEO Specialist & AI Search Strategist",
              "url": "https://narendradigital.in/",
              "sameAs": [
                "https://www.linkedin.com/in/narendra-kumar-kanna/"
              ]
            },
            "publisher": {
              "@type": "Person",
              "name": "Kanna Narendra Kumar"
            },
            "keywords": [
              "Why Do I Rank on Google but Don't Appear in ChatGPT",
              "ChatGPT Search Optimization",
              "AEO GEO AI Visibility",
              "OAI-SearchBot robots.txt",
              "Fan-Out Queries",
              "Hybrid Engine Optimization HEO",
              "AI Search Visibility Audit",
              "Entity Consistency for LLMs",
              "JavaScript SEO Rendering"
            ],
            "articleSection": "AEO/GEO, AI Visibility",
            "inLanguage": "en-US"
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://narendradigital.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://narendradigital.in/blog/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Why Do I Rank on Google but Don't Appear in ChatGPT?",
                "item": pageUrl
              }
            ]
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Why does my competitor appear in ChatGPT while my website only ranks on Google?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ranking on Google and being visible in AI search are not the same thing. Google ranks keyword-targeted URLs, whereas AI systems synthesize answers using distinct signals: crawler access (OAI-SearchBot), raw HTML availability without client-side JavaScript dependence, multi-layered fan-out query coverage, entity consistency across external sources, and atomic answer clarity."
                }
              },
              {
                "@type": "Question",
                "name": "How does robots.txt affect ChatGPT search visibility?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "OpenAI uses a specialized crawler named OAI-SearchBot for ChatGPT search results. If your robots.txt or WAF/CDN blocks OAI-SearchBot, or if you return 403 status codes, your content cannot be discovered or included in ChatGPT search answers."
                }
              },
              {
                "@type": "Question",
                "name": "What are fan-out queries in AI search?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Fan-out queries are interconnected, follow-up questions stemming from a single user intent. For example, a search for 'best project management software' naturally fans out into queries about pricing, Slack integrations, ease of use, remote support, and migration. AI search engines synthesize answers that satisfy the entire decision journey rather than a single head keyword."
                }
              },
              {
                "@type": "Question",
                "name": "What is Hybrid Engine Optimization (HEO)?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hybrid Engine Optimization (HEO) is an integrated search framework that makes a website Crawlable, Understandable, Relevant, Useful, and Consistent for both traditional search engines (Google, Bing) and AI answer engines (ChatGPT, Claude, Gemini)."
                }
              }
            ]
          }
        ]
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const auditStepsList = [
    {
      id: "step1",
      title: "Step 1: Check Access & Crawlability",
      desc: "Verify robots.txt allows OAI-SearchBot, check HTTP 200 OK, review WAF/CDN bot mitigation rules, and ensure noindex directives aren't misconfigured."
    },
    {
      id: "step2",
      title: "Step 2: Check Technical Foundation",
      desc: "Inspect indexability, canonical tags, clean XML sitemaps, internal linking pathways, rendering speed, and structured Schema markup."
    },
    {
      id: "step3",
      title: "Step 3: Understand Customer Intent",
      desc: "Identify what real customers ask before, during, and after making a purchasing decision rather than tracking isolated keyword search volumes."
    },
    {
      id: "step4",
      title: "Step 4: Build Content Around the Fan-Out",
      desc: "Cover the entire buyer decision journey by answering natural follow-up questions: pricing, ease of use, team fit, integrations, and competitor comparisons."
    },
    {
      id: "step5",
      title: "Step 5: Build a Consistent Entity",
      desc: "Ensure company name, products, locations, and descriptions match consistently across your website, LinkedIn, review directories, Reddit, and industry media."
    },
    {
      id: "step6",
      title: "Step 6: Test Real AI Visibility",
      desc: "Prompt ChatGPT and AI engines with real customer questions, recording brand mentions, cited URLs, source grounding, and competitor citations."
    }
  ];

  const completedAuditCount = Object.values(checkedAuditSteps).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#070a12] text-white selection:bg-cyan-500 selection:text-black">
      {/* Background Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-indigo-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed top-96 right-10 w-[500px] h-[350px] bg-cyan-600/10 rounded-full blur-[160px] pointer-events-none" />

      {/* Main Global Navigation Bar */}
      <Navbar />

      <main className="max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 relative z-10">
        {/* Article Breadcrumbs & Quick Utility Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <nav className="flex items-center gap-2 text-xs font-mono text-white/50">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <Link to="/blog/" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <span className="text-cyan-400 truncate">AEO / GEO &amp; AI Visibility</span>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/70 hover:text-cyan-400 px-3 py-1.5 rounded-full border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Lab</span>
            </Link>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-cyan-400 hover:text-white px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copied ? "Copied" : "Share"}</span>
            </button>
          </div>
        </div>

        {/* Article Header */}
        <header className="space-y-6 pb-8 border-b border-white/10">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider">
              AEO/GEO , AI Visibility
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider">
              Hybrid Engine Optimization (HEO)
            </span>
          </div>

          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.15]">
            Why Do I Rank on Google but Don't Appear in ChatGPT?
          </h1>

          <p className="text-base sm:text-lg text-white/70 leading-relaxed font-normal">
            Ranking on Google and being visible in AI search are not exactly the same thing. Discover why competitors appear in ChatGPT while you don't, how OAI-SearchBot works, the mechanics of fan-out queries, and the 6-step AI visibility audit protocol.
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 text-xs font-mono text-white/50">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-white/80 font-bold">Kanna Narendra Kumar</span>
              <span>&bull;</span>
              <span className="text-cyan-400">Senior SEO Specialist &amp; AI Search Strategist</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                <span>Oct 07, 2026</span>
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                <span>9 min read</span>
              </span>
            </div>
          </div>
        </header>

        {/* Key Takeaways Box — High Information Gain Container for AI Bots & Readers */}
        <section
          aria-label="Key Takeaways"
          className="my-7 p-5 sm:p-6 rounded-xl bg-[#0c1626] border-2 border-cyan-400 shadow-lg shadow-cyan-950/20"
        >
          <div className="flex items-center gap-2 mb-3.5 text-cyan-400">
            <Sparkles className="w-4 h-4 shrink-0 text-cyan-400" />
            <h2 className="text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-cyan-400 m-0">
              Key Takeaways
            </h2>
          </div>

          <ul className="list-disc pl-5 space-y-2.5 text-sm sm:text-[15px] text-slate-200 leading-relaxed marker:text-cyan-400">
            <li>
              <strong className="text-white">Google rankings ≠ ChatGPT visibility:</strong> Google and AI answer engines use fundamentally different discovery, retrieval, and evaluation signals.
            </li>
            <li>
              <strong className="text-white">Audit OAI-SearchBot crawler access:</strong> OpenAI's search crawler must be explicitly permitted in <code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1.5 py-0.5 rounded text-xs font-mono">robots.txt</code> and not blocked by WAFs, CDNs, or bot-mitigation challenges.
            </li>
            <li>
              <strong className="text-white">Deliver critical data in raw HTML:</strong> Avoid burying core business value propositions, product features, and pricing behind client-side JavaScript rendering.
            </li>
            <li>
              <strong className="text-white">Optimize for fan-out queries:</strong> AI users ask complete questions that fan out into follow-ups. Build content that answers the entire decision journey rather than isolated keywords.
            </li>
            <li>
              <strong className="text-white">Maintain cross-web entity consistency:</strong> Your website tells AI what you say about yourself; the rest of the web (LinkedIn, reviews, directories, Reddit) confirms whether others agree.
            </li>
            <li>
              <strong className="text-white">Hybrid Engine Optimization (HEO):</strong> Don't split SEO and AEO/GEO into separate strategies. Build a site that is Crawlable, Understandable, Relevant, Useful, and Consistent for both ecosystems.
            </li>
          </ul>
        </section>

        {/* Article Body — 100% Original Content Preserved Word-for-Word */}
        <article className="py-10 space-y-8 text-white/85 text-base sm:text-lg leading-relaxed font-sans">
          
          <div className="space-y-4">
            <p>You rank on Google.</p>
            <p>Your website has good pages.</p>
            <p>You have done SEO.</p>
            <p>But then you search for your business or product in ChatGPT and...</p>

            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-lg sm:text-xl shadow-xl my-6 not-italic">
              Your competitor appears. You don't.
            </blockquote>

            <p>This is becoming a common problem.</p>
            <p>The reason is simple:</p>

            <p className="text-white font-bold text-lg sm:text-xl">
              Ranking on Google and being visible in AI search are not exactly the same thing.
            </p>

            <p>
              Google and AI-powered search systems may use different signals to find, understand and select information.
            </p>

            <p>
              So if your website is only built to rank for keywords, you may be missing some important signals that AI systems need to understand your business.
            </p>

            <p>
              I usually look at this through <strong>three major areas</strong>.
            </p>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section 1 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              1. Your website may be blocking AI crawlers
            </h2>

            <p>This is one of the first things I check.</p>

            <p>
              Your website can be perfectly visible to Google but still have restrictions that affect AI search discovery.
            </p>

            <p>
              For example, OpenAI uses a crawler called <strong>OAI-SearchBot</strong> for ChatGPT search. OpenAI specifically recommends allowing OAI-SearchBot if you want your website content to be discovered and included in ChatGPT search results.
            </p>

            <p>So the first thing to check is:</p>

            <p className="text-cyan-300 font-mono font-bold text-lg">Your robots.txt</p>

            <p>Look for rules that may block AI crawlers.</p>

            <p>For example:</p>

            <pre className="p-4 rounded-xl bg-[#080d19] border border-white/10 text-cyan-300 font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto">
{`User-agent: OAI-SearchBot
Disallow: /`}
            </pre>

            <p>
              If something like this exists intentionally, your website may not be available for ChatGPT search.
            </p>

            <p>But robots.txt is not the only thing to check.</p>

            <p>You should also check:</p>

            <ul className="list-disc pl-6 space-y-2 text-white/90">
              <li>HTTP status codes</li>
              <li>403/401 responses</li>
              <li>CDN and WAF restrictions</li>
              <li>Bot protection</li>
              <li>Cloudflare rules</li>
              <li>Login requirements</li>
              <li>JavaScript challenges</li>
              <li>Noindex directives</li>
              <li>Canonical tags</li>
              <li>XML sitemaps</li>
              <li>Crawlable internal links</li>
            </ul>

            <p>
              OpenAI also notes that firewalls, CDNs, bot mitigation, authentication and other access controls can prevent its crawlers from accessing a page.
            </p>

            <h3 className="font-heading font-bold text-xl text-white pt-2">
              So ask yourself:
            </h3>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-cyan-200 font-semibold not-italic my-4">
              Can an AI crawler actually reach and read my important pages?
            </blockquote>

            <p>
              If the answer is no, content optimization will not solve the problem.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section 2 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              2. Your important content may depend too much on JavaScript
            </h2>

            <p>This is another area I check during an AI visibility audit.</p>

            <p>
              Modern websites are often built using React, Next.js, Angular, JavaScript-heavy applications and other frameworks.
            </p>

            <p>There is nothing wrong with using these technologies.</p>

            <p>
              The problem starts when the <strong>important information only becomes available after client-side rendering or other browser-side actions.</strong>
            </p>

            <p>Your most important information should be easy for crawlers to access.</p>

            <p>For example:</p>

            <ul className="list-disc pl-6 space-y-2 text-white/90">
              <li>What does your company do?</li>
              <li>What products do you sell?</li>
              <li>Who is the product for?</li>
              <li>What problems does it solve?</li>
              <li>What are the important features?</li>
              <li>What industries do you serve?</li>
              <li>What locations do you serve?</li>
              <li>What makes you different?</li>
              <li>What questions do customers ask?</li>
            </ul>

            <p>
              If this information is hidden behind complicated rendering, interaction, API calls or other client-side processes, you are creating unnecessary friction for search systems.
            </p>

            <p>
              Google has supported JavaScript rendering for many years, so this is <strong>not</strong> a simple case of “JavaScript is bad for SEO.”
            </p>

            <p>The better approach is:</p>

            <blockquote className="p-5 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-white font-semibold text-lg not-italic my-6 shadow-xl">
              Do not make critical information dependent on JavaScript if you can make it directly available in the page's HTML.
            </blockquote>

            <p>
              This makes your content easier to crawl, understand and process across different search environments.
            </p>
          </section>

          {/* Mid-Article AI Visibility Audit CTA Callout (~30% Scroll) */}
          <div className="my-8 p-6 sm:p-7 rounded-2xl bg-gradient-to-r from-[#0c1629] via-[#0f1d38] to-[#0c1426] border border-cyan-500/35 shadow-xl shadow-cyan-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 not-prose">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>AI Search Readiness Check</span>
              </div>
              <h3 className="font-heading font-bold text-lg sm:text-xl text-white leading-snug">
                Ranking on Google But Invisible in ChatGPT &amp; AI Overviews?
              </h3>
              <p className="text-xs sm:text-sm text-white/70 max-w-xl leading-relaxed">
                Find out exactly what is blocking your site from AI citations with a complete 4-layer AI Visibility Audit across crawlers, HTML rendering, and customer fan-out queries.
              </p>
            </div>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
            >
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section 3 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              3. You may be targeting keywords instead of user intent
            </h2>

            <p>This is probably the biggest content problem I see.</p>

            <p>Traditional SEO often starts with:</p>

            <div className="p-4 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-sm sm:text-base text-cyan-300 font-bold text-center tracking-wide overflow-x-auto">
              Keyword &rarr; Page &rarr; Ranking
            </div>

            <p>AI search changes the way we need to think.</p>

            <p>Users don't always ask AI systems using the exact keyword you are targeting.</p>

            <p>They ask complete questions.</p>

            <p>For example, you may target:</p>

            <p className="font-mono text-cyan-400 font-bold text-lg">“CRM software”</p>

            <p>But your potential customer may ask:</p>

            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/90 not-italic">
              What is the best CRM for a small sales team with 5 people?
            </blockquote>

            <p>Then they may ask:</p>

            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/90 not-italic">
              Which CRM is affordable for a startup?
            </blockquote>

            <p>Then:</p>

            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/90 not-italic">
              What should I check before choosing a CRM?
            </blockquote>

            <p>Then:</p>

            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/90 not-italic">
              HubSpot vs Salesforce — which is easier for a small company?
            </blockquote>

            <p>
              These are <strong>fan-out queries</strong>.
            </p>

            <p>One main question creates many related questions.</p>

            <p>
              AI-powered search experiences are increasingly designed around understanding the user's intent and providing synthesized answers rather than simply returning a list of pages.
            </p>

            <p>So your content should not only answer:</p>

            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-amber-400 text-amber-200 font-medium not-italic">
              “What keyword do I want to rank for?”
            </blockquote>

            <p>It should answer:</p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-emerald-400 text-emerald-200 font-semibold not-italic">
              “What questions will my customer ask before, during and after making this decision?”
            </blockquote>

            <p>
              To learn how to systematically uncover these multi-condition questions and build content around buyer problems, read my comprehensive guide on{" "}
              <Link
                to="/blog/how-to-do-keyword-research-for-ai-visibility/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                how to do keyword research for AI visibility
              </Link>
              .
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section: What are Fan-Out Queries? */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              What are Fan-Out Queries?
            </h2>

            <p>Imagine someone searches:</p>

            <p className="font-mono text-cyan-400 font-bold text-lg">
              “Best project management software for a small company.”
            </p>

            <p>That is only the beginning.</p>

            <p>Their next questions could be:</p>

            <ul className="list-disc pl-6 space-y-2 text-white/90">
              <li>Which software is easiest to use?</li>
              <li>Which one is cheapest?</li>
              <li>Does it support automation?</li>
              <li>Does it integrate with Slack?</li>
              <li>Does it have time tracking?</li>
              <li>Which one is better for remote teams?</li>
              <li>Asana vs Monday — which is better?</li>
              <li>What are the disadvantages?</li>
              <li>How much does it cost?</li>
              <li>Can I migrate from another tool?</li>
              <li>Is it suitable for a 10-person company?</li>
            </ul>

            <p>Your website should help answer these questions.</p>

            <p>
              This does <strong>not</strong> mean creating 50 thin articles.
            </p>

            <p>
              It means creating useful content that covers the <strong>decision journey</strong>.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section: There Are Other Signals You Should Not Ignore */}
          <section className="space-y-6">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              There Are Other Signals You Should Not Ignore
            </h2>

            <p>The three areas above are the first things I would investigate.</p>

            <p>But AI visibility is not controlled by three factors alone.</p>

            <p>
              There are several other problems that can reduce your chances of being discovered, understood or cited.
            </p>

            {/* 4 */}
            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                4. Your content looks exactly like everyone else's
              </h3>

              <p>If ten competitors say:</p>

              <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-rose-400 text-rose-200 not-italic">
                “We provide innovative solutions to help businesses grow.”
              </blockquote>

              <p>You are not giving search systems much useful information.</p>

              <p>AI systems need useful, specific information.</p>

              <p>Instead of saying:</p>

              <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-rose-400 text-rose-200 not-italic">
                “We are a leading HR software company.”
              </blockquote>

              <p>Explain:</p>

              <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-emerald-400 text-emerald-200 font-medium not-italic">
                “Our HR platform helps Indian companies automate payroll, PF, ESI and employee attendance for teams between 50 and 5,000 employees.”
              </blockquote>

              <p className="text-white font-bold">
                Specific information is easier to understand than marketing language.
              </p>
            </div>

            {/* 5 */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                5. Your entity is not consistent across the internet
              </h3>

              <p>Your website is not the only place where your business exists.</p>

              <p>Search engines and AI systems can find information about your company from:</p>

              <ul className="list-disc pl-6 space-y-1.5 text-white/90">
                <li>Your website</li>
                <li>LinkedIn</li>
                <li>Business directories</li>
                <li>Review websites</li>
                <li>Industry websites</li>
                <li>News articles</li>
                <li>Partner websites</li>
                <li>Product listings</li>
                <li>Customer discussions</li>
                <li>Reddit</li>
                <li>YouTube</li>
                <li>Other authoritative websites</li>
              </ul>

              <p>
                If your company description, product name, location, category or other important information is different across these sources, it becomes harder to build a consistent picture of your business.
              </p>

              <p>Think of it this way:</p>

              <blockquote className="p-5 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-white font-semibold text-lg not-italic my-4 space-y-2 shadow-xl">
                <p>Your website tells AI what you say about yourself.</p>
                <p className="text-cyan-300">The rest of the internet helps confirm whether other sources agree with you.</p>
              </blockquote>
            </div>

            {/* 6 */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                6. Your website may not be technically strong enough
              </h3>

              <p>A beautiful website does not automatically mean a technically strong website.</p>

              <p>Check the basics:</p>

              <ul className="list-disc pl-6 space-y-1.5 text-white/90">
                <li>Is the page indexable?</li>
                <li>Does it return a 200 status?</li>
                <li>Is the canonical correct?</li>
                <li>Is the content visible without unnecessary interaction?</li>
                <li>Are important pages internally linked?</li>
                <li>Are there orphan pages?</li>
                <li>Are redirects working correctly?</li>
                <li>Is the sitemap clean?</li>
                <li>Are important pages accidentally blocked?</li>
                <li>Is structured data implemented correctly?</li>
                <li>Is the website fast enough?</li>
                <li>Are important pages accessible on mobile?</li>
              </ul>

              <p className="text-white font-bold">
                Before trying advanced AEO or GEO tactics, fix the foundation.
              </p>
            </div>

            {/* 7 */}
            <div className="space-y-4 pt-4 border-t border-white/10">
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                7. Your website may answer questions, but not make the answer easy to use
              </h3>

              <p>This is an underrated problem.</p>

              <p>AI systems need to understand information quickly.</p>

              <p>Compare these two statements.</p>

              <h4 className="font-heading font-bold text-lg text-rose-300 pt-1">Version 1</h4>
              <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-rose-400 text-white/80 not-italic">
                “Our innovative, next-generation platform provides businesses with a comprehensive and powerful ecosystem designed to enable organizations to achieve their digital transformation goals.”
              </blockquote>

              <h4 className="font-heading font-bold text-lg text-emerald-300 pt-1">Version 2</h4>
              <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-emerald-400 text-white font-medium not-italic">
                “Our platform helps HR teams manage payroll, attendance, leave and employee records from one system.”
              </blockquote>

              <p>Which one is easier to understand?</p>

              <p className="font-mono text-emerald-400 font-bold text-xl">Version 2.</p>

              <p>Write for humans first.</p>

              <p>But make the information clear enough that machines can understand it too.</p>
            </div>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section: So How Do You Fix It? */}
          <section className="space-y-6">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              So How Do You Fix It?
            </h2>

            <p>I would not start by publishing 100 new blog posts.</p>

            <p>
              Start with an <strong>AI visibility audit.</strong>
            </p>

            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-lg sm:text-xl text-cyan-300">
                Step 1: Check access
              </h3>
              <p>Check:</p>
              <div className="p-3 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-xs sm:text-sm text-cyan-300 font-bold text-center overflow-x-auto">
                robots.txt &rarr; OAI-SearchBot &rarr; other relevant crawlers &rarr; WAF/CDN &rarr; HTTP status &rarr; noindex
              </div>
              <p>Make sure important pages can actually be accessed.</p>
              <p>
                OpenAI specifically states that allowing OAI-SearchBot helps make website content eligible for discovery in ChatGPT search.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-lg sm:text-xl text-cyan-300">
                Step 2: Check the technical foundation
              </h3>
              <p>Review:</p>
              <div className="p-3 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-xs sm:text-sm text-cyan-300 font-bold text-center overflow-x-auto">
                Indexability &rarr; Canonicals &rarr; Sitemaps &rarr; Internal links &rarr; Rendering &rarr; Structured data &rarr; Page speed
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-lg sm:text-xl text-cyan-300">
                Step 3: Understand your customer's questions
              </h3>
              <p>Don't start with only keywords.</p>
              <p>Start with:</p>
              <p className="font-mono text-white font-bold">What is the customer trying to achieve?</p>
              <p>Then map the questions around that goal.</p>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-lg sm:text-xl text-cyan-300">
                Step 4: Build content around the fan-out
              </h3>
              <p>One topic should answer the main question and the important follow-up questions.</p>
              <p>Don't create content just because a keyword has search volume.</p>
              <p>
                Create it because <strong>your customer needs the answer.</strong>
              </p>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-lg sm:text-xl text-cyan-300">
                Step 5: Build a consistent entity
              </h3>
              <p>Make sure your:</p>
              <ul className="list-disc pl-6 space-y-1 text-white/90">
                <li>Company name</li>
                <li>Products</li>
                <li>Services</li>
                <li>Locations</li>
                <li>People</li>
                <li>Categories</li>
                <li>Descriptions</li>
              </ul>
              <p>are consistent across important sources.</p>
            </div>

            <div className="space-y-4 pt-2">
              <h3 className="font-heading font-bold text-lg sm:text-xl text-cyan-300">
                Step 6: Test AI visibility
              </h3>
              <p>
                Don't assume your work is successful because you implemented schema or published ten articles.
              </p>
              <p>Actually test questions your customers ask.</p>
              <p>For example:</p>

              <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-indigo-100 not-italic">
                “What are the best [product category] for [customer type]?”
              </blockquote>

              <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-indigo-100 not-italic">
                “Which companies provide [service] in [location]?”
              </blockquote>

              <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-indigo-100 not-italic">
                “[Your company] vs [competitor]”
              </blockquote>

              <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-indigo-100 not-italic">
                “What is the best solution for [specific problem]?”
              </blockquote>

              <p>Then record:</p>

              <ul className="list-disc pl-6 space-y-1.5 text-cyan-300 font-mono text-sm sm:text-base">
                <li>Was my company mentioned?</li>
                <li>Was my website cited?</li>
                <li>Was my competitor mentioned instead?</li>
                <li>Was the information correct?</li>
                <li>What source did the AI use?</li>
              </ul>

              <p>This gives you a much better picture of your AI visibility.</p>
            </div>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Section: SEO + AI Visibility Should Not Be Two Separate Strategies */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              SEO + AI Visibility Should Not Be Two Separate Strategies
            </h2>

            <p>This is where I believe many companies are going wrong.</p>

            <p>You don't need:</p>

            <p className="font-mono text-white/90 font-bold">SEO strategy</p>

            <p>and then another completely separate:</p>

            <p className="font-mono text-cyan-400 font-bold">AEO/GEO strategy</p>

            <p>You need a strategy that makes your website:</p>

            <div className="p-4 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-xs sm:text-sm text-cyan-300 font-bold text-center tracking-wide overflow-x-auto">
              Crawlable + Understandable + Relevant + Useful + Consistent
            </div>

            <p>for both traditional search and AI-powered search.</p>

            <p>
              That is the thinking behind <strong>Hybrid Engine Optimization (HEO)</strong>.
            </p>

            <p>The goal is not:</p>

            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “How do I rank on ChatGPT?”
            </blockquote>

            <p>The better question is:</p>

            <blockquote className="p-5 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-semibold text-lg not-italic my-6 shadow-xl">
              “How do I make my business easy for both search engines and AI systems to discover, understand and confidently recommend?”
            </blockquote>

            <p>Google rankings still matter.</p>
            <p>Technical SEO still matters.</p>
            <p>Content still matters.</p>

            <p>
              But the content now needs to answer the <strong>real questions behind the keyword</strong>.
            </p>

            <p>
              And your website needs to provide enough clear information for search engines and AI systems to understand what you actually do.
            </p>

            <p className="text-white font-bold text-lg sm:text-xl">
              Don't create content for Google.
            </p>

            <p className="text-cyan-400 font-bold text-lg sm:text-xl">
              Don't create content for ChatGPT.
            </p>

            <p className="text-white text-lg sm:text-xl">
              Create the best possible answer for the person who is searching.
            </p>

            <p className="text-white/90">
              Then make sure both search engines and AI systems can access, understand and trust that answer.
            </p>
          </section>

        </article>

        {/* Interactive 6-Step AI Visibility Audit Checklist Tool */}
        <section
          aria-label="Interactive AI Visibility Audit Tool"
          className="my-10 p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#0c1428] via-[#090e1c] to-[#070b16] border border-cyan-500/30 shadow-2xl relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative z-10 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-white/10">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">
                    Interactive AI Visibility Audit Tool
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-white/60 font-normal">
                  Run through the 6-step protocol to benchmark your website's readiness for ChatGPT, Claude, and AI search engines.
                </p>
              </div>

              <div className="text-right sm:text-right shrink-0">
                <span className="text-xs font-mono text-cyan-400 font-bold">
                  {completedAuditCount} of {auditStepsList.length} Complete
                </span>
                <div className="w-36 h-2 rounded-full bg-white/10 mt-1.5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-all duration-300"
                    style={{ width: `${(completedAuditCount / auditStepsList.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {auditStepsList.map((step) => {
                const isChecked = !!checkedAuditSteps[step.id];
                return (
                  <div
                    key={step.id}
                    onClick={() => toggleAuditStep(step.id)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer select-none text-left flex items-start gap-3.5 ${
                      isChecked
                        ? "bg-cyan-950/30 border-cyan-500/50 text-white shadow-lg shadow-cyan-950/30"
                        : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20 hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="mt-0.5 shrink-0 text-cyan-400">
                      {isChecked ? (
                        <CheckSquare className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className={`font-heading font-bold text-sm ${isChecked ? "text-cyan-300" : "text-white"}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/60 leading-relaxed font-normal">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs sm:text-sm text-white/75">
                Want us to run this 6-step AI Visibility Audit on your website and deliver an actionable roadmap?
              </p>
              <Link
                to="/ai-visibility-audit/"
                className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-all"
              >
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Book Your AI Visibility Audit</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>

          </div>
        </section>

        {/* Related Deep Dives & Case Studies */}
        <section className="my-12 space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              <span>Related Technical Playbooks &amp; Case Studies</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/blog/how-to-do-keyword-research-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase">
                  Intent &amp; AI Visibility
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-cyan-400 transition-colors leading-snug">
                  How to Do Keyword Research for AI Visibility
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Customer-intent-first approach using GSC, Reddit, customer reviews, and sales conversations to build a Question Memory.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-cyan-400 font-semibold">
                <span>Read Intent Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/can-ai-search-engines-crawl-understand-cite-website/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 uppercase">
                  Technical SEO &amp; AEO
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-indigo-400 transition-colors leading-snug">
                  Can AI Search Engines Crawl, Understand and Cite Your Website?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  The complete 4-layer AI visibility framework and technical checklist for OAI-SearchBot, ClaudeBot, and Google-Extended.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-indigo-400 font-semibold">
                <span>Read Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/how-to-audit-a-website-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-purple-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-500/10 text-purple-300 border border-purple-500/20 uppercase">
                  10-Point Technical Checklist
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-purple-400 transition-colors leading-snug">
                  How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  10 essential checks covering OAI-SearchBot access, JavaScript rendering parity, customer fan-out questions, and citations.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-purple-400 font-semibold">
                <span>Read Audit Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/how-to-measure-ai-search-visibility-for-free/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-emerald-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 uppercase">
                  Free Analytics Framework
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-emerald-400 transition-colors leading-snug">
                  How to Measure AI Search Visibility for Free
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Step-by-step 5-source framework using GSC, Bing Webmaster Tools, Server Logs, GA4, and Clarity without paid tools.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <span>Read Free Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>

        {/* Author Bio Card */}
        <footer className="mt-12 pt-8 border-t border-white/10">
          <div className="p-6 sm:p-7 rounded-3xl bg-[#0a0f1d] border border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left shadow-2xl">
            <img
              src="/narendra-workstation.jpg"
              alt="Kanna Narendra Kumar"
              className="w-24 h-24 rounded-2xl object-cover object-[center_20%] border border-cyan-500/40 shrink-0 shadow-xl"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/narendra-avatar.jpg";
              }}
            />
            <div className="space-y-2.5 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="font-heading font-bold text-lg text-white">
                    Written by Kanna Narendra Kumar
                  </h4>
                  <p className="text-xs font-mono text-cyan-400">
                    Senior SEO Specialist &amp; AI Search Strategist
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/narendra-kumar-kanna/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-xs text-white/80 font-mono transition-colors self-center sm:self-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>LinkedIn Profile</span>
                </a>
              </div>
              <p className="text-xs sm:text-sm text-white/75 leading-relaxed font-sans">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic visibility and high-intent inbound pipeline for global B2B SaaS. Pioneer of the Hybrid Engine Optimization (HEO) framework unifying traditional search with generative AI platforms. Available globally for strategic advisory and high-impact enterprise execution.
              </p>
            </div>
          </div>
        </footer>

        {/* Collaboration CTA Footer */}
        <section className="mt-10 p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Zap className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
            Need High-Impact Search &amp; AI Execution?
          </h3>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Whether you are navigating Google AI Overviews, preparing a CMS migration, or scaling high-converting organic SQLs, let&apos;s connect directly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai-visibility-audit/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Schedule Strategy Discussion</span>
              <ArrowUpRight className="w-4 h-4 text-cyan-400" />
            </Link>
          </div>
        </section>

      </main>
    </div>
  );
}
